/* eslint-disable no-underscore-dangle */
import { v4 as uuidV4 } from 'uuid';
import i18next from 'i18next';
import { snakeCase } from 'lodash';
import {
  ACTION_RESOURCE_FREQUENCY_VALUES,
  ACTION_RESOURCE_TYPES,
  ACTION_RESOURCE_BASIS_REQUEST_PAYLOAD_MAP,
  ACTION_RESOURCE_BASIS_RESPONSE_PAYLOAD_MAP,
  ACTION_RESOURCE_STAFF_TYPE_REQUEST_PAYLOAD_MAP,
  ACTION_RESOURCE_STAFF_TYPE_RESPONSE_PAYLOAD_MAP,
  ACTION_RESOURCE_STAFF_DAY_TYPE_REQUEST_PAYLOAD_MAP,
  ACTION_RESOURCE_STAFF_DAY_TYPE_RESPONSE_PAYLOAD_MAP,
  ACTION_RESOURCE_FREQUENCY_RESPONSE_VALUES_MAP,
  ACTION_RESOURCE_TYPES_RESPONSE_PAYLOAD_MAP,
} from 'src/components/CapActionResource/constants';
import logger from 'src/utils/logger';

export default class CapActionResourceModel {

  constructor(
    {
      id = uuidV4(),
      actionId = null,
      frequency = null,
      basis = null,
      staffType = null,
      staffDayType = null,
      staffDayValue = null,
      numberOfDays = null,
      currencyId = null,
      costs = null,
      details = null,
      resourceType = ACTION_RESOURCE_TYPES.NO_RESOURCE,
      scrollToResouce = false,
    }) {

    this._id = id;
    this._actionId = actionId;
    this._frequency = frequency;
    this._basis = basis;
    this._staffType = staffType;
    this._staffDayType = staffDayType;
    this._staffDayValue = staffDayValue;
    this._numberOfDays = numberOfDays;
    this._currencyId = currencyId;
    this._costs = costs;
    this._details = details;
    this._resourceType = resourceType;
    this._scrollToResouce = scrollToResouce;
  }

  get id() {
    return this._id;
  }

  set id(value) {
    this._id = value;
  }

  get frequency() {
    return this._frequency;
  }

  set frequency(value) {
    this._frequency = value;
  }

  get basis() {
    return this._basis;
  }

  set basis(value) {
    this._basis = value;
  }

  get staffType() {
    return this._staffType;
  }

  set staffType(value) {
    this._staffType = value;
  }

  get staffDayType() {
    return this._staffDayType;
  }

  set staffDayType(value) {
    this._staffDayType = value;
  }

  get staffDayValue() {
    return this._staffDayValue;
  }

  set staffDayValue(value) {
    this._staffDayValue = value;
  }

  get numberOfDays() {
    return this._numberOfDays;
  }

  set numberOfDays(value) {
    this._numberOfDays = value;
  }

  get currencyId() {
    return this._currencyId;
  }

  set currencyId(value) {
    this._currencyId = value;
  }

  get costs() {
    return this._costs;
  }

  set costs(value) {
    this._costs = value;
  }

  get details() {
    return this._details;
  }

  set details(value) {
    this._details = value;
  }

  get resourceType() {
    return this._resourceType;
  }

  set resourceType(value) {
    this._resourceType = value;
  }

  get actionId() {
    return this._actionId;
  }

  set actionId(value) {
    this._actionId = value;
  }

  get scrollToResouce() {
    return this._scrollToResouce;
  }

  set scrollToResouce(value) {
    this._scrollToResouce = value;
  }

  isVirtualType() {
    return this._resourceType === ACTION_RESOURCE_TYPES.NO_RESOURCE;
  }

  isInvestmentType() {
    return this._resourceType === ACTION_RESOURCE_TYPES.INVESTMENT;
  }

  isStaffDaysType() {
    return this._resourceType === ACTION_RESOURCE_TYPES.NUMBER_OFF_STAFF_DAYS;
  }

  isRecurring() {
    return this._frequency === ACTION_RESOURCE_FREQUENCY_VALUES.RECURRING;
  }

  isOneTime() {
    return this._frequency === ACTION_RESOURCE_FREQUENCY_VALUES.ONE_TIME;
  }

  isNew() {
    return Number.isNaN(Number(this._id));
  }

  getData() {
    return {
      id: this._id,
      actionId: this._actionId,
      frequency: this._frequency,
      basis: this._basis,
      staffType: this._staffType,
      staffDayType: this._staffDayType,
      staffDayValue: this._staffDayValue,
      numberOfDays: this._numberOfDays,
      currencyId: this._currencyId,
      costs: this._costs,
      details: this._details,
      resourceType: this._resourceType,
      scrollToResouce: this._scrollToResouce,
    };
  }

  reInit(
    {
      id = null,
      actionId = null,
      frequency = null,
      basis = null,
      staffType = null,
      staffDayType = null,
      staffDayValue = null,
      numberOfDays = null,
      currencyId = null,
      costs = null,
      details = null,
      resourceType = null,
      scrollToResouce = false,
    }) {
    this._id = id;
    this._actionId = actionId;
    this._frequency = frequency;
    this._basis = basis;
    this._staffType = staffType;
    this._staffDayType = staffDayType;
    this._staffDayValue = staffDayValue;
    this._numberOfDays = numberOfDays;
    this._currencyId = currencyId;
    this._costs = costs;
    this._details = details;
    this._resourceType = resourceType;
    this._scrollToResouce = scrollToResouce;
  }
  
  static ValidateBasis(resource) {
    return value => {
      let error;
      if (resource.isRecurring() && !value) {
        error = i18next.t('REQUIRED');
      }
      return error;
    };
  }

  static ValidateStaffDayType(resource) {
    return value => {
      let error;
      if (resource.isStaffDaysType() && !value) {
        error = i18next.t('REQUIRED');
      }
      return error;
    };
  }

  static ValidateStaffDayValue(resource) {
    return value => {
      let error;
      if (resource.isStaffDaysType()) {
        if (!value) {
          error = i18next.t('REQUIRED');
        } else if (Number.parseFloat(value) === 0) {
          error = i18next.t('GREATER_THAN_ZERO_REQUIRED');
        } else if (!/^\d+(\.\d)?$/.test(value)) {
          error = i18next.t('INVALID_FORMAT');
        }
      }
      return error;
    };
  }

  static ValidateCurrency(resource) {
    return value => {
      let error;
      if (resource.isInvestmentType() && !value) {
        error = i18next.t('REQUIRED');
      }
      return error;
    };
  }

  static ValidateDetails(resource) {
    return value => {
      let error;
      if (!value) {
        error = i18next.t('REQUIRED');
      }
      return error;
    };
  }

  static ValidateCosts(resource) {
    return value => {
      let error;
      if (resource.isInvestmentType()) {
        if (!value) {
          error = i18next.t('REQUIRED');
        } else if (Number.parseFloat(value) === 0) {
          error = i18next.t('GREATER_THAN_ZERO_REQUIRED');
        } else if (!/^\d+(\.\d{1,2})?$/.test(value)) {
          error = i18next.t('INVALID_FORMAT');
        }
      }
      return error;
    };
  }

  static TransformFormDataToRequestData(resource, formValues) {
    if (!formValues || Object.keys(formValues).length === 0) {
      logger.error('Invalid resource data');
      return undefined;
    }

    const data = { ...formValues };
    if (resource.isNew()) {
      delete data.id;
    }
    data.frequency = data.frequency ? snakeCase(data.frequency) : null;
    data.basis = data.basis ? ACTION_RESOURCE_BASIS_REQUEST_PAYLOAD_MAP[data.basis] : null;
    data.staffType = data.staffType ? ACTION_RESOURCE_STAFF_TYPE_REQUEST_PAYLOAD_MAP[data.staffType] : null;
    data.staffDayType = data.staffDayType ? ACTION_RESOURCE_STAFF_DAY_TYPE_REQUEST_PAYLOAD_MAP[data.staffDayType] : null;
    data.staffDayValue = data.staffDayValue ? Number.parseFloat(data.staffDayValue) : null;
    data.costs = data.costs ? Number.parseFloat(data.costs) : null;
    data.resourceType = data.resourceType ? snakeCase(data.resourceType) : null;

    return data;

  }
}

export const CapActionResourceObjectFactory = (resource) => {
  return {
    id: resource.id,
    actionId: resource.actionId,
    basis: ACTION_RESOURCE_BASIS_RESPONSE_PAYLOAD_MAP[resource.basis] || null,
    staffType: ACTION_RESOURCE_STAFF_TYPE_RESPONSE_PAYLOAD_MAP[resource.staffType] || null,
    costs: resource.costs,
    currencyId: resource.currencyId,
    details: resource.details,
    frequency: ACTION_RESOURCE_FREQUENCY_RESPONSE_VALUES_MAP[resource.frequency] || null,
    staffDayType: ACTION_RESOURCE_STAFF_DAY_TYPE_RESPONSE_PAYLOAD_MAP[resource.staffDayType] || null,
    staffDayValue: resource.staffDayValue,
    numberOfDays: resource.numberOfStaffDays,
    resourceType: ACTION_RESOURCE_TYPES_RESPONSE_PAYLOAD_MAP[resource.resourceType] || null,
  };
};
